import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, ArrowLeft, PersonStanding, Trash2 } from 'lucide-react';
import { Skeleton } from '../../../skeletons';
import { toast } from 'react-hot-toast';
import residencesService from '../../../services/carehome_manager/residencesService';
import frontBodyAsset from '../../../assets/body_map/body-map-front.svg';
import backBodyAsset from '../../../assets/body_map/body-map-back.svg';
import { Select } from '../../../components';

// ─── Shared constants (mirrored from AddIncidentModal) ────────────────────────

const SEVERITY_COLOR = { Mild: '#22C55E', Moderate: '#F97316', Severe: '#EF4444' };
const SEV_RANK = { Mild: 1, Moderate: 2, Severe: 3 };

const MARK_TYPES = [
    'Bruise', 'Cut / Laceration', 'Graze / Abrasion', 'Swelling',
    'Redness', 'Skin tear', 'Burn / Scald', 'Bump / Lump', 'Pressure mark', 'Other',
];

// ─── Injury status (image 2) ──────────────────────────────────────────────────

const STATUS_GROUPS = {
    Open:   ['New', 'Monitoring', 'Healing'],
    Closed: ['Resolved', 'OK / No concern'],
};
const STATUS_ORDER = ['New', 'Monitoring', 'Healing', 'Resolved', 'OK / No concern'];
const STATUS_META = {
    'New':             { color: '#3B82F6', group: 'Open' },
    'Monitoring':      { color: '#F97316', group: 'Open' },
    'Healing':         { color: '#14B8A6', group: 'Open' },
    'Resolved':        { color: '#22C55E', group: 'Closed' },
    'OK / No concern': { color: '#6B7280', group: 'Closed' },
};
const normStatus  = (s) => (STATUS_META[s] ? s : 'New');
const statusColor = (s) => STATUS_META[normStatus(s)].color;
const isOpenStatus = (s) => STATUS_META[normStatus(s)].group === 'Open';

const highestSeverity = (markers) => {
    if (!markers.length) return 'Mild';
    return markers.reduce((best, m) =>
        (SEV_RANK[m.severity] || 1) > (SEV_RANK[best] || 1) ? m.severity : best, 'Mild');
};

const FRONT_ZONES = [
    { id: 'f-head',    label: 'Head',                 cx: 120, cy: 48,  d: 'M120,8 C99,8 86,26 86,48 C86,70 99,88 120,88 C141,88 154,70 154,48 C154,26 141,8 120,8 Z' },
    { id: 'f-neck',    label: 'Neck',                 cx: 120, cy: 93,  d: 'M106,82 L134,82 Q137,82 137,86 L137,104 Q137,107 134,107 L106,107 Q103,107 103,104 L103,86 Q103,82 106,82 Z' },
    { id: 'f-chest',   label: 'Chest',                cx: 120, cy: 165, d: 'M62,128 C62,112 78,107 96,107 L144,107 C162,107 178,112 178,128 L177,222 L63,222 Z' },
    { id: 'f-abdomen', label: 'Abdomen',              cx: 120, cy: 278, d: 'M63,222 L177,222 C176,250 174,280 172,300 C170,320 162,336 150,342 L90,342 C78,336 70,320 68,300 C66,280 64,250 63,222 Z' },
    { id: 'f-groin',   label: 'Groin / private area', cx: 120, cy: 358, d: 'M104,340 L136,340 L132,360 C130,372 125,378 120,378 C115,378 110,372 108,360 Z' },
    { id: 'f-r-uarm',  label: 'Right upper arm',      cx: 47,  cy: 175, d: 'M62,128 C53,116 41,117 37,131 L33,221 L62,221 Z' },
    { id: 'f-l-uarm',  label: 'Left upper arm',       cx: 193, cy: 175, d: 'M178,128 C187,116 199,117 203,131 L207,221 L178,221 Z' },
    { id: 'f-r-farm',  label: 'Right forearm',        cx: 47,  cy: 270, d: 'M33,221 L62,221 L61,298 C61,314 55,327 47,334 L42,332 C35,325 31,313 31,298 L33,221 Z' },
    { id: 'f-l-farm',  label: 'Left forearm',         cx: 193, cy: 270, d: 'M207,221 L178,221 L179,298 C179,314 185,327 193,334 L198,332 C205,325 209,313 209,298 L207,221 Z' },
    { id: 'f-r-hand',  label: 'Right hand',           cx: 47,  cy: 363, d: 'M41,330 L60,332 C66,330 72,335 72,342 C72,349 66,353 60,352 L62,385 C61,395 56,400 49,400 L44,400 C38,400 34,395 33,386 L31,360 C30,350 34,338 41,330 Z' },
    { id: 'f-l-hand',  label: 'Left hand',            cx: 193, cy: 363, d: 'M199,330 L180,332 C174,330 168,335 168,342 C168,349 174,353 180,352 L178,385 C179,395 184,400 191,400 L196,400 C202,400 206,395 207,386 L209,360 C210,350 206,338 199,330 Z' },
    { id: 'f-r-thigh', label: 'Right thigh',          cx: 92,  cy: 390, d: 'M68,342 L116,342 L114,438 L73,438 L68,342 Z' },
    { id: 'f-l-thigh', label: 'Left thigh',           cx: 148, cy: 390, d: 'M172,342 L124,342 L126,438 L167,438 L172,342 Z' },
    { id: 'f-r-knee',  label: 'Right knee',           cx: 93,  cy: 457, d: 'M73,438 L114,438 L112,476 L76,476 Z' },
    { id: 'f-l-knee',  label: 'Left knee',            cx: 147, cy: 457, d: 'M167,438 L126,438 L128,476 L164,476 Z' },
    { id: 'f-r-lleg',  label: 'Right lower leg',      cx: 93,  cy: 527, d: 'M76,476 L112,476 L107,560 C107,572 101,578 93,578 L84,578 C77,578 74,572 74,560 L76,476 Z' },
    { id: 'f-l-lleg',  label: 'Left lower leg',       cx: 147, cy: 527, d: 'M164,476 L128,476 L133,560 C133,572 139,578 147,578 L156,578 C163,578 166,572 166,560 L164,476 Z' },
    { id: 'f-r-foot',  label: 'Right foot',           cx: 87,  cy: 599, d: 'M76,576 L106,576 L108,602 C108,615 98,622 86,622 L78,622 C69,620 67,610 70,600 Z' },
    { id: 'f-l-foot',  label: 'Left foot',            cx: 153, cy: 599, d: 'M164,576 L134,576 L132,602 C132,615 142,622 154,622 L162,622 C171,620 173,610 170,600 Z' },
];

const BACK_ZONES = [
    { id: 'b-head',     label: 'Head',                  cx: 120, cy: 48,  d: 'M120,8 C99,8 86,26 86,48 C86,70 99,88 120,88 C141,88 154,70 154,48 C154,26 141,8 120,8 Z' },
    { id: 'b-neck',     label: 'Neck',                  cx: 120, cy: 93,  d: 'M106,82 L134,82 Q137,82 137,86 L137,104 Q137,107 134,107 L106,107 Q103,107 103,104 L103,86 Q103,82 106,82 Z' },
    { id: 'b-l-shldr',  label: 'Left shoulder',         cx: 90,  cy: 129, d: 'M62,128 C62,112 78,107 96,107 L118,107 L118,150 L63,150 Z' },
    { id: 'b-r-shldr',  label: 'Right shoulder',        cx: 150, cy: 129, d: 'M178,128 C178,112 162,107 144,107 L122,107 L122,150 L177,150 Z' },
    { id: 'b-uback',    label: 'Upper back',            cx: 120, cy: 191, d: 'M63,150 L177,150 L177,232 L63,232 Z' },
    { id: 'b-lback',    label: 'Lower back',            cx: 120, cy: 268, d: 'M63,232 L177,232 C176,260 174,286 172,304 L68,304 C66,286 64,260 63,232 Z' },
    { id: 'b-buttocks', label: 'Buttocks / sacral area',cx: 120, cy: 330, d: 'M68,304 L172,304 C172,330 164,350 146,356 C133,360 122,350 120,344 C118,350 107,360 94,356 C76,350 68,330 68,304 Z' },
    { id: 'b-l-uarm',   label: 'Left upper arm',        cx: 47,  cy: 175, d: 'M62,128 C53,116 41,117 37,131 L33,221 L62,221 Z' },
    { id: 'b-r-uarm',   label: 'Right upper arm',       cx: 193, cy: 175, d: 'M178,128 C187,116 199,117 203,131 L207,221 L178,221 Z' },
    { id: 'b-l-farm',   label: 'Left forearm',          cx: 47,  cy: 270, d: 'M33,221 L62,221 L61,298 C61,314 55,327 47,334 L42,332 C35,325 31,313 31,298 L33,221 Z' },
    { id: 'b-r-farm',   label: 'Right forearm',         cx: 193, cy: 270, d: 'M207,221 L178,221 L179,298 C179,314 185,327 193,334 L198,332 C205,325 209,313 209,298 L207,221 Z' },
    { id: 'b-l-hand',   label: 'Left hand',             cx: 47,  cy: 363, d: 'M41,330 L60,332 C66,330 72,335 72,342 C72,349 66,353 60,352 L62,385 C61,395 56,400 49,400 L44,400 C38,400 34,395 33,386 L31,360 C30,350 34,338 41,330 Z' },
    { id: 'b-r-hand',   label: 'Right hand',            cx: 193, cy: 363, d: 'M199,330 L180,332 C174,330 168,335 168,342 C168,349 174,353 180,352 L178,385 C179,395 184,400 191,400 L196,400 C202,400 206,395 207,386 L209,360 C210,350 206,338 199,330 Z' },
    { id: 'b-l-thigh',  label: 'Left thigh',            cx: 92,  cy: 409, d: 'M68,342 L116,342 L112,476 L76,476 Z' },
    { id: 'b-r-thigh',  label: 'Right thigh',           cx: 148, cy: 409, d: 'M172,342 L124,342 L128,476 L164,476 Z' },
    { id: 'b-l-calf',   label: 'Left calf',             cx: 93,  cy: 518, d: 'M76,476 L112,476 L107,560 C107,572 101,578 93,578 L84,578 C77,578 74,572 74,560 L76,476 Z' },
    { id: 'b-r-calf',   label: 'Right calf',            cx: 147, cy: 518, d: 'M164,476 L128,476 L133,560 C133,572 139,578 147,578 L156,578 C163,578 166,572 166,560 L164,476 Z' },
    { id: 'b-l-heel',   label: 'Left heel',             cx: 91,  cy: 587, d: 'M78,576 L104,576 Q106,576 106,580 L106,598 L76,598 L76,580 Q76,576 78,576 Z' },
    { id: 'b-r-heel',   label: 'Right heel',            cx: 149, cy: 587, d: 'M162,576 L136,576 Q134,576 134,580 L134,598 L164,598 L164,580 Q164,576 162,576 Z' },
    { id: 'b-l-foot',   label: 'Left foot',             cx: 87,  cy: 612, d: 'M76,598 L106,598 L108,610 C108,620 98,626 86,626 L78,626 C69,624 67,616 70,608 Z' },
    { id: 'b-r-foot',   label: 'Right foot',            cx: 153, cy: 612, d: 'M164,598 L134,598 L132,610 C132,620 142,626 154,626 L162,626 C171,624 173,616 170,608 Z' },
];

const ALL_ZONES = [...FRONT_ZONES, ...BACK_ZONES];

const FIG_W = 240, FIG_H = 660;

// Markers reach us in two shapes: the canonical body-map shape
// ({ pinX, pinY, zoneId }) and the older mobile incident shape
// ({ x, y as 0..1 fractions, no zoneId }). Normalise so every marker has a
// usable pinX/pinY and a resolved zoneId — otherwise marks recorded on the
// mobile incident form would not pin or tint on this tab.
const normalizeMarker = (m) => {
    let { pinX, pinY, zoneId } = m;
    if ((pinX == null || pinY == null) && m.x != null && m.y != null) {
        pinX = Number(m.x) * FIG_W;
        pinY = Number(m.y) * FIG_H;
    }
    if (!zoneId && pinX != null && pinY != null) {
        const zones = (m.side || 'front') === 'front' ? FRONT_ZONES : BACK_ZONES;
        let best = null, bestD = Infinity;
        zones.forEach(z => {
            const dx = z.cx - pinX, dy = z.cy - pinY, d = dx * dx + dy * dy;
            if (d < bestD) { bestD = d; best = z; }
        });
        if (best) zoneId = best.id;
    }
    return { ...m, pinX, pinY, zoneId };
};

// ─── Interactive body figure SVG ──────────────────────────────────────────────

function BodyFigureSVG({ side, allMarkers, activeUid, activeZoneId, pendingPin, onPinClick, onZoneClick }) {
    const zones    = side === 'front' ? FRONT_ZONES : BACK_ZONES;
    const baseImg  = side === 'front' ? frontBodyAsset : backBodyAsset;

    // Only OPEN injuries are pinned on the figure — closed ones are hidden here
    // (they remain in the list, greyed out).
    const sideMarkers = allMarkers.filter(m => m.side === side && isOpenStatus(m.status));

    const getSvgPoint = (e) => {
        const svg = e.currentTarget.ownerSVGElement;
        const pt  = svg.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        return pt.matrixTransform(svg.getScreenCTM().inverse());
    };

    return (
        <svg viewBox="0 0 240 660" xmlns="http://www.w3.org/2000/svg" className="w-full" style={{ display: 'block' }}>
            <style>{`
                .bm-zone { cursor: crosshair; transition: filter 0.12s; }
                .bm-zone:hover { filter: brightness(0.95); }
            `}</style>

            {/* Base body figure (asset) */}
            <image href={baseImg} x="0" y="0" width="240" height="660" preserveAspectRatio="xMidYMid meet" />

            {zones.map(zone => {
                // Zone colour reflects open injuries only. Zones without an open
                // injury stay transparent so the asset's neutral fill shows through.
                const marks    = allMarkers.filter(m => m.zoneId === zone.id && isOpenStatus(m.status));
                const topMark  = marks[0];
                const isActive = zone.id === activeZoneId;
                const fill     = isActive ? '#BFDBFE' : topMark ? SEVERITY_COLOR[topMark.severity] + '40' : 'transparent';
                const stroke   = isActive ? '#3B82F6' : topMark ? SEVERITY_COLOR[topMark.severity] : 'transparent';
                return (
                    <path key={zone.id} className="bm-zone" d={zone.d}
                        fill={fill} stroke={stroke} strokeWidth={isActive || topMark ? 1.6 : 0}
                        onClick={e => onZoneClick(zone, getSvgPoint(e))}>
                        <title>{zone.label}</title>
                    </path>
                );
            })}

            {sideMarkers.map(mark => {
                const col      = SEVERITY_COLOR[mark.severity];
                const stCol    = statusColor(mark.status);
                const isActive = mark.uid === activeUid;
                return (
                    <g key={mark.uid} transform={`translate(${mark.pinX},${mark.pinY})`}
                        style={{ cursor: 'pointer' }}
                        onClick={e => { e.stopPropagation(); onPinClick(mark.uid); }}>
                        {isActive && <circle r="16" fill={col} opacity="0.25" />}
                        <circle r="12" fill={col} opacity="0.2" />
                        <circle r="8.5" fill={col} stroke="#fff" strokeWidth={isActive ? 3 : 2.5} />
                        <text y="3.3" textAnchor="middle" fontSize="10" fontWeight="700" fill="#fff"
                            style={{ pointerEvents: 'none', userSelect: 'none' }}>
                            {mark.displayIndex + 1}
                        </text>
                        {/* status dot */}
                        <circle cx="6.5" cy="-6.5" r="3.2" fill={stCol} stroke="#fff" strokeWidth="1.2"
                            style={{ pointerEvents: 'none' }} />
                    </g>
                );
            })}

            {/* Pending pin while adding a new mark */}
            {pendingPin && pendingPin.side === side && (
                <g transform={`translate(${pendingPin.x},${pendingPin.y})`} style={{ pointerEvents: 'none' }}>
                    <circle r="12" fill="#1e293b" opacity="0.15" />
                    <circle r="7"  fill="#1e293b" stroke="#fff" strokeWidth="2.5" />
                    <circle r="2.5" fill="#fff" />
                </g>
            )}
        </svg>
    );
}

// ─── Status dropdown (grouped, mirrors image 2) ───────────────────────────────

function StatusSelect({ value, onChange }) {
    return (
        <Select value={normStatus(value)} onChange={(v) => onChange(v)}
            options={Object.values(STATUS_GROUPS).flat().map((o) => ({ value: o, label: o }))}
            className="w-full" />
    );
}

// ─── Mark editor / add panel ──────────────────────────────────────────────────

function MarkEditorPanel({ mode, zone, side, marker, form, setForm, saving, onSave, onDelete, onCancel }) {
    return (
        <div className="flex flex-col gap-3 p-5">
            {/* Header */}
            <div className="flex items-center gap-2">
                <button type="button" onClick={onCancel}
                    className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#2c313a] transition shrink-0">
                    <ArrowLeft className="w-3.5 h-3.5" />
                </button>
                <span className="text-[15px] font-bold text-gray-800 dark:text-gray-100">
                    {mode === 'edit' ? 'Edit injury mark' : 'New injury mark'}
                </span>
            </div>

            {/* Zone context */}
            <div className="flex items-center gap-3 bg-indigo-50 dark:bg-[#1a2540] border border-indigo-100 dark:border-blue-900 rounded-xl px-3.5 py-3">
                <div className="w-9 h-9 rounded-xl bg-[#2563EB] flex items-center justify-center shrink-0">
                    <PersonStanding className="w-5 h-5 text-white" />
                </div>
                <div>
                    <p className="text-[13px] font-bold text-gray-800 dark:text-gray-100">{zone?.label}</p>
                    <p className="text-[11px] text-indigo-500 capitalize">{side} view · position pinned</p>
                </div>
            </div>

            {/* Incident context (edit mode) */}
            {mode === 'edit' && marker && (
                <div className="border border-gray-100 dark:border-gray-800 rounded-xl px-3.5 py-2.5">
                    <h4 className="text-[11px] font-bold text-[#2563EB] uppercase tracking-wide mb-1.5">Linked incident</h4>
                    {[
                        ['Type',      marker.incidentType],
                        ['Date',      marker.incidentDate],
                        ['Reference', marker.incidentRef],
                        ['Logged by', marker.completedBy],
                    ].map(([k, v]) => (
                        <div key={k} className="flex justify-between text-[11.5px] py-1 border-b border-gray-50 dark:border-gray-800 last:border-0">
                            <span className="text-gray-400 dark:text-gray-500">{k}</span>
                            <span className="font-medium text-gray-700 dark:text-gray-200">{v || '—'}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Mark type */}
            <div>
                <label className="text-[12px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Mark type <span className="text-red-500">*</span></label>
                <Select value={form.type} onChange={(v) => setForm(f => ({ ...f, type: v }))}
                    placeholder="Select type..."
                    options={MARK_TYPES.map((t) => ({ value: t, label: t }))}
                    className="w-full" />
            </div>

            {/* Severity */}
            <div>
                <label className="text-[12px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Severity <span className="text-red-500">*</span></label>
                <div className="flex gap-2">
                    {['Mild', 'Moderate', 'Severe'].map(s => (
                        <button key={s} type="button" onClick={() => setForm(f => ({ ...f, severity: s }))}
                            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] font-semibold border transition ${form.severity === s ? 'border-transparent shadow-sm' : 'bg-white dark:bg-[#2c313a] text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                            style={form.severity === s ? { background: SEVERITY_COLOR[s] + '18', borderColor: SEVERITY_COLOR[s], color: SEVERITY_COLOR[s] } : {}}>
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: form.severity === s ? SEVERITY_COLOR[s] : '#D1D5DB' }} />
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            {/* Status */}
            <div>
                <label className="text-[12px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Status</label>
                <StatusSelect value={form.status} onChange={v => setForm(f => ({ ...f, status: v }))} />
            </div>

            {/* Notes */}
            <div>
                <label className="text-[12px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Notes</label>
                <textarea rows={3} placeholder="Describe the mark, suspected cause, action taken..."
                    value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-[12px] bg-white dark:bg-[#2c313a] text-gray-800 dark:text-gray-100 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-1">
                {mode === 'edit' && (
                    <button type="button" onClick={onDelete} disabled={saving}
                        className="flex items-center gap-1.5 px-4 py-2 border border-red-200 dark:border-red-900/50 text-red-500 dark:text-red-400 rounded-lg text-[12px] font-medium hover:bg-red-50 dark:hover:bg-red-900/10 transition disabled:opacity-40">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                )}
                <button type="button" onClick={onCancel} disabled={saving}
                    className="flex-1 py-2 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-[12px] font-medium hover:bg-gray-50 dark:hover:bg-[#2c313a] transition disabled:opacity-40">
                    Cancel
                </button>
                <button type="button" onClick={onSave} disabled={!form.type || saving}
                    className="flex-1 py-2 bg-[#2563EB] text-white rounded-lg text-[12px] font-semibold hover:bg-blue-700 transition disabled:opacity-40">
                    {saving ? 'Saving...' : mode === 'edit' ? 'Save changes' : 'Save mark'}
                </button>
            </div>
        </div>
    );
}

// ─── Stats bar (clickable filters: all / open / closed / by status) ───────────

function StatsBar({ markers, activeFilter, onFilter }) {
    const counts = useMemo(() => {
        const c = { New: 0, Monitoring: 0, Healing: 0, Resolved: 0, 'OK / No concern': 0 };
        markers.forEach(m => { c[normStatus(m.status)]++; });
        const open   = c.New + c.Monitoring + c.Healing;
        const closed = c.Resolved + c['OK / No concern'];
        return { ...c, open, closed, total: markers.length };
    }, [markers]);

    const bigCards = [
        { key: 'all',    label: 'Total',    value: counts.total,  color: '#2563EB' },
        { key: 'open',   label: 'Open',     value: counts.open,   color: '#F97316' },
        { key: 'closed', label: 'Closed',   value: counts.closed, color: '#22C55E' },
    ];

    return (
        <div className="mb-5">
            <div className="grid grid-cols-3 gap-3 mb-3">
                {bigCards.map(c => {
                    const active = activeFilter === c.key;
                    return (
                        <button key={c.key} type="button" onClick={() => onFilter(c.key)}
                            className={`text-left bg-white dark:bg-[#262b33] border rounded-xl px-4 py-3 shadow-sm transition ${active ? 'border-current ring-1' : 'border-gray-100 dark:border-gray-800 hover:border-gray-300'}`}
                            style={active ? { color: c.color, borderColor: c.color } : {}}>
                            <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">{c.label}</p>
                            <p className="text-[24px] font-bold leading-tight" style={{ color: c.color }}>{c.value}</p>
                        </button>
                    );
                })}
            </div>
            <div className="flex flex-wrap gap-2">
                {STATUS_ORDER.map(s => {
                    const active = activeFilter === s;
                    return (
                        <button key={s} type="button" onClick={() => onFilter(s)}
                            className={`flex items-center gap-1.5 bg-white dark:bg-[#262b33] border rounded-full pl-2 pr-3 py-1 text-[11.5px] shadow-sm transition ${active ? 'ring-1' : 'border-gray-100 dark:border-gray-800 hover:border-gray-300'}`}
                            style={active ? { borderColor: statusColor(s), color: statusColor(s) } : {}}>
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: statusColor(s) }} />
                            <span className={active ? 'font-medium' : 'text-gray-500 dark:text-gray-400'}>{s}</span>
                            <span className="font-bold text-gray-800 dark:text-gray-100">{counts[s]}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const BodyMapTab = () => {
    const { id } = useParams();
    const [loading,    setLoading]    = useState(true);
    const [records,    setRecords]    = useState([]);
    const [activeUid,  setActiveUid]  = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');

    // Editor state
    const [editorMode, setEditorMode] = useState(null);   // null | 'add' | 'edit'
    const [activeZone, setActiveZone] = useState(null);
    const [pendingPin, setPendingPin] = useState(null);
    const [editTarget, setEditTarget] = useState(null);   // { incidentId, markerId }
    const [markForm,   setMarkForm]   = useState({ type: '', severity: 'Mild', status: 'New', notes: '' });
    const [saving,     setSaving]     = useState(false);

    const parseMarkers = (val) => {
        if (Array.isArray(val)) return val;
        if (typeof val === 'string' && val) { try { return JSON.parse(val); } catch { return []; } }
        return [];
    };

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await residencesService.getResidentIncidents(id);
            setRecords(response.data?.records || []);
        } catch (err) {
            console.error('Error fetching body map data:', err);
            toast.error('Could not load body map data');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { if (id) fetchData(); }, [id, fetchData]);

    // Flatten markers across all incidents
    const allMarkers = useMemo(() => {
        const flat = [];
        records.forEach(inc => {
            parseMarkers(inc.body_map_markers).forEach((raw, idx) => {
                const m    = normalizeMarker(raw);
                const zone = ALL_ZONES.find(z => z.id === m.zoneId);
                flat.push({
                    ...m,
                    zoneLabel:        m.zoneLabel || zone?.label || '',
                    status:           normStatus(m.status),
                    uid:              `${inc.id}-${m.id ?? idx}`,
                    displayIndex:     flat.length,
                    incidentId:       inc.id,
                    incidentType:     inc.cause,
                    incidentDate:     inc.date,
                    incidentRef:      inc.reference,
                    incidentSeverity: inc.severity,
                    completedBy:      inc.completedBy,
                });
            });
        });
        return flat;
    }, [records]);

    // Clear selection if it no longer exists (after refetch); never auto-select
    useEffect(() => {
        if (activeUid && !allMarkers.some(m => m.uid === activeUid)) setActiveUid(null);
    }, [allMarkers, activeUid]);

    const activeMarker = allMarkers.find(m => m.uid === activeUid);

    // List honours the active stat filter
    const filteredMarkers = useMemo(() => {
        if (statusFilter === 'all')    return allMarkers;
        if (statusFilter === 'open')   return allMarkers.filter(m => isOpenStatus(m.status));
        if (statusFilter === 'closed') return allMarkers.filter(m => !isOpenStatus(m.status));
        return allMarkers.filter(m => normStatus(m.status) === statusFilter);
    }, [allMarkers, statusFilter]);

    // Toggle a filter off when its already active (back to "all")
    const handleFilter = (key) => setStatusFilter(prev => (prev === key ? 'all' : key));

    // ── Editor open / close ──
    const closeEditor = () => { setEditorMode(null); setActiveZone(null); setPendingPin(null); setEditTarget(null); };

    const openAdd = (zone, svgPoint) => {
        if (saving) return;
        const side = zone.id.startsWith('f-') ? 'front' : 'back';
        setActiveZone(zone);
        setPendingPin({ x: svgPoint.x, y: svgPoint.y, side });
        setEditTarget(null);
        setMarkForm({ type: '', severity: 'Mild', status: 'New', notes: '' });
        setEditorMode('add');
    };

    const openEdit = (uid) => {
        const mark = allMarkers.find(m => m.uid === uid);
        if (!mark) return;
        setActiveUid(uid);
        const zone = ALL_ZONES.find(z => z.id === mark.zoneId);
        setActiveZone(zone);
        setPendingPin({ x: mark.pinX, y: mark.pinY, side: mark.side });
        setEditTarget({ incidentId: mark.incidentId, markerId: mark.id });
        setMarkForm({ type: mark.type || '', severity: mark.severity || 'Mild', status: normStatus(mark.status), notes: mark.notes || '' });
        setEditorMode('edit');
    };

    // ── Persist ──
    const persistMarkers = async (incidentId, newMarkers) => {
        await residencesService.updateIncident(id, incidentId, {
            body_map_markers: newMarkers,
            severity: highestSeverity(newMarkers),
        });
    };

    const handleSave = async () => {
        if (!markForm.type || !activeZone || !pendingPin) return;
        setSaving(true);
        try {
            if (editorMode === 'edit' && editTarget) {
                const rec = records.find(r => r.id === editTarget.incidentId);
                const existing = parseMarkers(rec?.body_map_markers);
                const newMarkers = existing.map(m => m.id === editTarget.markerId
                    ? { ...m, type: markForm.type, severity: markForm.severity, status: markForm.status, notes: markForm.notes }
                    : m);
                await persistMarkers(editTarget.incidentId, newMarkers);
                toast.success('Injury mark updated');
            } else {
                // Direct add → create a lightweight standalone incident holding this mark
                const marker = {
                    id: Date.now(),
                    zoneId: activeZone.id,
                    zoneLabel: activeZone.label,
                    side: pendingPin.side,
                    pinX: pendingPin.x,
                    pinY: pendingPin.y,
                    type: markForm.type,
                    severity: markForm.severity,
                    status: markForm.status,
                    notes: markForm.notes,
                };
                await residencesService.createIncident(id, {
                    incident_type: 'Injury',
                    incident_date: new Date().toISOString(),
                    description: `Injury mark recorded directly on the body map (${activeZone.label}).`,
                    body_map_markers: [marker],
                    severity: markForm.severity,
                    notify_manager: true,
                });
                toast.success('Injury mark added');
            }
            closeEditor();
            await fetchData();
        } catch (err) {
            toast.error('Could not save injury mark');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!editTarget) return;
        setSaving(true);
        try {
            const rec = records.find(r => r.id === editTarget.incidentId);
            const existing = parseMarkers(rec?.body_map_markers);
            const newMarkers = existing.filter(m => m.id !== editTarget.markerId);
            await persistMarkers(editTarget.incidentId, newMarkers);
            toast.success('Injury mark removed');
            closeEditor();
            await fetchData();
        } catch (err) {
            toast.error('Could not remove injury mark');
        } finally {
            setSaving(false);
        }
    };

    const activeZoneId = activeZone?.id || null;

    return (
        <div className="mt-6 animate-in fade-in duration-300">

            {/* ── Stats (clickable filters) ── */}
            {!loading && allMarkers.length > 0 && (
                <StatsBar markers={allMarkers} activeFilter={statusFilter} onFilter={handleFilter} />
            )}

            <div className="flex flex-col lg:flex-row gap-6">

                {/* ── Left: Body figures ── */}
                <div className="w-full lg:w-[55%] bg-white dark:bg-[#262b33] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-6 flex flex-col">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-[18px] font-semibold text-gray-800 dark:text-gray-100">Body Map</h2>
                        <div className="flex gap-3">
                            {Object.entries(SEVERITY_COLOR).map(([s, c]) => (
                                <span key={s} className="flex items-center gap-1.5 text-[12px] text-gray-500 dark:text-gray-400">
                                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c }} />{s}
                                </span>
                            ))}
                        </div>
                    </div>
                    <p className="text-[12px] text-gray-400 dark:text-gray-500 italic mb-3">Tap a body region to record a new injury · tap a numbered pin to edit</p>

                    {loading ? (
                        <Skeleton className="flex-1 w-full min-h-[400px] rounded-2xl" />
                    ) : (
                        <div className="border border-gray-100 dark:border-gray-700 rounded-xl bg-[#F8FAFC] dark:bg-[#1b1f26] p-3">
                            <div className="grid grid-cols-2 gap-2">
                                <div className="flex flex-col items-center">
                                    <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">Front</span>
                                    <BodyFigureSVG side="front" allMarkers={allMarkers} activeUid={activeUid}
                                        activeZoneId={activeZoneId} pendingPin={pendingPin}
                                        onPinClick={openEdit} onZoneClick={openAdd} />
                                </div>
                                <div className="flex flex-col items-center">
                                    <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">Back</span>
                                    <BodyFigureSVG side="back" allMarkers={allMarkers} activeUid={activeUid}
                                        activeZoneId={activeZoneId} pendingPin={pendingPin}
                                        onPinClick={openEdit} onZoneClick={openAdd} />
                                </div>
                            </div>
                            <p className="text-center text-[10px] text-gray-400 dark:text-gray-500 mt-2">
                                L / R shown from the resident's perspective
                            </p>
                        </div>
                    )}
                </div>

                {/* ── Right: Detail / editor panel ── */}
                <div className="w-full lg:w-[45%] bg-white dark:bg-[#262b33] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm flex flex-col">

                    {loading ? (
                        <Skeleton className="flex-1 w-full min-h-[300px] rounded-2xl" />
                    ) : editorMode ? (
                        <MarkEditorPanel
                            mode={editorMode}
                            zone={activeZone}
                            side={pendingPin?.side}
                            marker={activeMarker}
                            form={markForm}
                            setForm={setMarkForm}
                            saving={saving}
                            onSave={handleSave}
                            onDelete={handleDelete}
                            onCancel={closeEditor}
                        />
                    ) : allMarkers.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center min-h-[300px]">
                            <div className="w-14 h-14 rounded-full bg-gray-50 dark:bg-[#2c313a] flex items-center justify-center mb-3">
                                <svg viewBox="0 0 240 660" className="w-7 h-7 opacity-30">
                                    <path d="M120,8 C99,8 86,26 86,48 C86,70 99,88 120,88 C141,88 154,70 154,48 C154,26 141,8 120,8 Z M63,107 L177,107 L177,400 L63,400 Z" fill="#6B7280" />
                                </svg>
                            </div>
                            <p className="text-[13px] font-medium text-gray-500 dark:text-gray-400">No body map marks recorded</p>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1 max-w-[240px]">Tap any region on the figure to record an injury directly, or log one from the Accident &amp; Incident tab.</p>
                        </div>
                    ) : (
                        /* Default view — the recorded marks list only (full height) */
                        <div className="flex flex-col h-full p-5 min-h-[400px]">
                            <div className="flex items-center justify-between mb-3 shrink-0">
                                <span className="text-[13px] font-bold text-gray-700 dark:text-gray-300">
                                    {statusFilter === 'all' ? 'All recorded marks'
                                        : statusFilter === 'open' ? 'Open marks'
                                        : statusFilter === 'closed' ? 'Closed marks'
                                        : `${statusFilter} marks`}
                                </span>
                                <span className="bg-gray-100 dark:bg-[#2c313a] text-gray-600 dark:text-gray-400 text-[11px] font-bold px-2.5 py-0.5 rounded-full">
                                    {filteredMarkers.length}
                                </span>
                            </div>

                            {filteredMarkers.length === 0 ? (
                                <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-400 dark:text-gray-500">
                                    <p className="text-[12px]">No marks match this filter.</p>
                                    <button type="button" onClick={() => setStatusFilter('all')}
                                        className="text-[12px] text-[#2563EB] font-medium mt-1 hover:underline">Show all</button>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-1.5 flex-1 overflow-y-auto">
                                    {filteredMarkers.map(m => {
                                        const closed = !isOpenStatus(m.status);
                                        return (
                                        <button key={m.uid} onClick={() => openEdit(m.uid)}
                                            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all border border-transparent hover:border-blue-200 dark:hover:border-blue-800 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 ${closed ? 'bg-gray-100/60 dark:bg-[#20242c] opacity-60' : 'bg-gray-50 dark:bg-[#262b33]'}`}>
                                            <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                                                style={{ background: closed ? '#9CA3AF' : SEVERITY_COLOR[m.severity] }}>
                                                {m.displayIndex + 1}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[12px] font-semibold text-gray-800 dark:text-gray-200 truncate">{m.type}</p>
                                                <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">{m.zoneLabel} &middot; {m.incidentRef}</p>
                                                <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
                                                    <span className="text-gray-400 dark:text-gray-500">Recorded by</span>{' '}
                                                    <span className="font-medium text-gray-600 dark:text-gray-300">{m.completedBy || 'Unknown'}</span>
                                                    {m.incidentDate ? ` · ${m.incidentDate}` : ''}
                                                </p>
                                            </div>
                                            <div className="flex flex-col items-end gap-1 shrink-0">
                                                <span className="text-[10px] font-semibold capitalize" style={{ color: SEVERITY_COLOR[m.severity] }}>
                                                    {m.severity}
                                                </span>
                                                <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: statusColor(m.status) }}>
                                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor(m.status) }} />
                                                    {normStatus(m.status)}
                                                </span>
                                            </div>
                                        </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default BodyMapTab;
